import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Phone, User } from 'lucide-react';
import { chatAPI } from '../services/api';
import { trackLeadSubmission } from '../utils/googleAds';

const STORAGE_KEY = 'conti_chat_session_id_v2';
const HISTORY_KEY = 'conti_chat_history_v2';
const LEGACY_KEYS = ['conti_chat_session_id', 'conti_chat_history'];
export const CHAT_RESET_EVENT = 'conti-chat-reset';
const GREETING = "Hi, I'm Alexander Conti's AI assistant. Ask me anything about Brantford-area homes, neighbourhoods, mortgages, or book a showing.";
const greetingMessages = () => [{ role: 'assistant', content: GREETING }];

export function clearChatStorage() {
  [STORAGE_KEY, HISTORY_KEY, ...LEGACY_KEYS].forEach((k) => localStorage.removeItem(k));
  window.dispatchEvent(new Event(CHAT_RESET_EVENT));
}
const AI_DISCLOSURE_KEY = 'conti_chat_ai_disclosure_seen';

function genSessionId() {
  return 'cb-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
}

const LiveChatWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = genSessionId();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  });
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffSubmitted, setHandoffSubmitted] = useState(false);
  const [visitor, setVisitor] = useState({ name: '', email: '', phone: '' });
  const [showAiDisclosure, setShowAiDisclosure] = useState(() => {
    try { return !localStorage.getItem(AI_DISCLOSURE_KEY); } catch { return true; }
  });
  const dismissAiDisclosure = () => {
    try { localStorage.setItem(AI_DISCLOSURE_KEY, '1'); } catch {/* ignore */}
    setShowAiDisclosure(false);
  };
  const scrollRef = useRef(null);

  // Restore previous chat history from localStorage
  useEffect(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
      if (Array.isArray(cached) && cached.length > 0) {
        setMessages(cached);
      } else {
        setMessages(greetingMessages());
      }
    } catch {
      setMessages(greetingMessages());
    }
  }, []);

  // Wipe chat + start a fresh session on logout
  useEffect(() => {
    const reset = () => {
      const id = genSessionId();
      localStorage.setItem(STORAGE_KEY, id);
      setSessionId(id);
      setMessages(greetingMessages());
      setInput('');
      setVisitor({ name: '', email: '', phone: '' });
      setHandoffOpen(false);
      setHandoffSubmitted(false);
    };
    window.addEventListener(CHAT_RESET_EVENT, reset);
    return () => window.removeEventListener(CHAT_RESET_EVENT, reset);
  }, []);

  // Persist & autoscroll
  useEffect(() => {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(messages));
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    try {
      const res = await chatAPI.sendMessage(sessionId, text, visitor);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
      // Explicit transfer request → show contact form
      if (res.intent && res.intent.intent === 'request_live_agent') {
        setHandoffOpen(true);
      }
      // Booking request without contact info → show contact form framed as "calendar invite"
      if (res.booking_intent && res.booking_intent._needs_contact) {
        setHandoffOpen(true);
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: "Sorry, I'm having trouble. Please text or call 226-934-4107." },
      ]);
    } finally {
      setSending(false);
    }
  };

  const [handoffSubmitting, setHandoffSubmitting] = useState(false);
  const [handoffConsent, setHandoffConsent] = useState(false);

  const submitHandoff = async (e) => {
    e?.preventDefault();
    if (handoffSubmitting) return;
    if (!visitor.email && !visitor.phone) {
      alert('Please share at least an email or phone so I can reach you.');
      return;
    }
    if (!handoffConsent) {
      alert('Please agree to the Terms of Use and Privacy Policy to continue.');
      return;
    }
    setHandoffSubmitting(true);
    // Immediate optimistic UI feedback so the button feels responsive
    setMessages((m) => [...m, { role: 'assistant', content: '✓ Got it — sending your info now…' }]);
    try {
      // Resend the LAST user message so the AI sees the contact info now & may
      // book the appointment if there was a pending booking_intent
      const res = await chatAPI.handoff(sessionId, {
        visitor_name: visitor.name,
        visitor_email: visitor.email,
        visitor_phone: visitor.phone,
        note: 'Visitor submitted contact info from chat widget.',
      });
      setHandoffOpen(false);
      setHandoffSubmitted(true);
      setMessages((m) => [...m, { role: 'assistant', content: res.message || 'Thanks! I will reach out shortly.' }]);
      // Fire Google Ads conversion — high-intent chat lead
      try { trackLeadSubmission('live_chat_handoff'); } catch (_) { /* ignore */ }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: 'Hmm, that didn\'t go through. Please call/text 226-934-4107 directly.' }]);
    } finally {
      setHandoffSubmitting(false);
    }
  };

  return (
    <>
      {/* Hide widget on admin pages */}
      {location.pathname.startsWith('/admin') ? null : (
        <>
      {/* Floating bubble */}
      {!open && (
        <button
          data-testid="open-chat-widget"
          onClick={() => setOpen(true)}
          className="bg-red-600 hover:bg-red-700 text-white rounded-full shadow-2xl flex items-center gap-2 transition-transform hover:scale-105 px-4 py-4 sm:py-4"
          style={{
            position: 'fixed',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
            right: '16px',
            zIndex: 2147483647,
          }}
          aria-label="Open chat with Alexander Conti"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="font-semibold text-sm sm:text-base">Chat</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed inset-x-2 bottom-2 sm:inset-x-auto sm:bottom-5 sm:right-5 z-[9999] sm:w-[92vw] sm:max-w-sm h-[80vh] sm:h-[70vh] max-h-[640px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-red-600 to-red-700 text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-bold flex items-center gap-2">
                <span className="inline-block w-2 h-2 bg-green-400 rounded-full" />
                Alexander Conti
              </div>
              <div className="text-xs opacity-90">AI Assistant — replies instantly</div>
            </div>
            <button
              data-testid="close-chat-widget"
              onClick={() => setOpen(false)}
              className="hover:bg-red-800 rounded-full p-1"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* AI disclosure badge — shown once per visitor, then dismissed forever */}
          {showAiDisclosure && (
            <div
              data-testid="ai-disclosure-badge"
              className="px-3 py-2.5 bg-amber-50 border-b border-amber-200 flex items-start gap-2 text-[11.5px] text-amber-900 leading-snug"
            >
              <span aria-hidden className="flex-shrink-0 mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-200 text-amber-900 font-bold text-[10px]">i</span>
              <div className="flex-1">
                <span className="font-semibold">Powered by AI.</span>{' '}
                Chats and any phone calls placed through this site may be recorded
                for quality and follow-up. By continuing, you consent to our{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-700">Privacy Policy</a>{' '}
                &amp;{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-amber-700">Terms</a>.
              </div>
              <button
                data-testid="dismiss-ai-disclosure"
                onClick={dismissAiDisclosure}
                className="flex-shrink-0 ml-1 px-2 py-0.5 rounded-md bg-amber-200 hover:bg-amber-300 text-amber-900 text-[11px] font-semibold transition-colors"
                aria-label="Dismiss AI disclosure notice"
              >
                Got it
              </button>
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-red-600 text-white rounded-br-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-3 py-2 rounded-2xl text-sm text-gray-500">
                  <span className="inline-flex gap-1">
                    <span className="animate-bounce">•</span>
                    <span className="animate-bounce" style={{ animationDelay: '0.15s' }}>•</span>
                    <span className="animate-bounce" style={{ animationDelay: '0.3s' }}>•</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Handoff form (inline) */}
          {handoffOpen && !handoffSubmitted && (
            <form
              data-testid="handoff-form"
              onSubmit={submitHandoff}
              className="p-3 border-t border-gray-200 bg-yellow-50 space-y-2"
            >
              <div className="text-xs font-medium text-gray-700 flex items-center gap-1">
                <User className="w-3 h-3" /> Quick contact info so I can reach you:
              </div>
              <input
                data-testid="handoff-name"
                placeholder="Your name"
                value={visitor.name}
                onChange={(e) => setVisitor({ ...visitor, name: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <input
                data-testid="handoff-phone"
                type="tel"
                placeholder="Phone (I'll call/text)"
                value={visitor.phone}
                onChange={(e) => setVisitor({ ...visitor, phone: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <input
                data-testid="handoff-email"
                type="email"
                placeholder="Email"
                value={visitor.email}
                onChange={(e) => setVisitor({ ...visitor, email: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
              <label htmlFor="handoff-consent" className="flex items-start gap-2 text-[11px] text-gray-600 cursor-pointer leading-snug">
                <input
                  data-testid="handoff-consent"
                  id="handoff-consent"
                  type="checkbox"
                  checked={handoffConsent}
                  onChange={(e) => setHandoffConsent(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer flex-shrink-0"
                />
                <span>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">Terms</a>{' '}
                  &amp;{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">Privacy Policy</a>{' '}
                  and consent to be contacted.
                </span>
              </label>
              <div className="flex gap-2">
                <button
                  data-testid="handoff-submit"
                  type="submit"
                  disabled={handoffSubmitting || !handoffConsent}
                  className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white text-sm py-2 rounded-lg font-medium flex items-center justify-center gap-1 transition-colors"
                >
                  {handoffSubmitting ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Connecting…
                    </>
                  ) : (
                    <>
                      <Phone className="w-4 h-4" /> Connect me
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setHandoffOpen(false)}
                  disabled={handoffSubmitting}
                  className="px-3 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="p-3 border-t border-gray-200 bg-white flex gap-2"
          >
            <input
              data-testid="chat-input"
              type="text"
              placeholder="Type a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={sending}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={!input.trim() || sending}
              className="bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white rounded-full p-2.5 transition"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
        </>
      )}
    </>
  );
};

export default LiveChatWidget;
